import type {
  BuildingView,
  CodexEntryView,
  CodexFamilyView,
  DecodeMoveView,
  DecodePuzzleDefinition,
  MissionStepView,
  PlayerVerticalSliceView,
  ResourceCode,
  ResourceDisplay,
} from '@aztec-realms/shared-types';

import { loadApiVerticalSlice, submitApiVerticalSliceCommand } from './api-vertical-slice';

const resources: readonly ResourceDisplay[] = [
  { code: 'maize', label: 'Maize', amount: 600 },
  { code: 'stone', label: 'Stone', amount: 350 },
  { code: 'timber', label: 'Timber', amount: 300 },
  { code: 'jade', label: 'Jade', amount: 20 },
  { code: 'knowledge', label: 'Knowledge', amount: 0 },
];

const buildings: readonly BuildingView[] = [
  {
    id: 'solar-shrine-01',
    type: 'solar_shrine',
    label: 'Solar Shrine',
    level: 1,
    gridX: -1,
    gridY: -4,
    orientation: 0,
    variant: 'dawn_a',
    districtCode: 'lakeside_outpost',
    visualTier: 0,
    availableBranches: [],
    state: 'upgrade_available',
    upgradeCost: [
      { code: 'maize', label: 'Maize', amount: 120 },
      { code: 'stone', label: 'Stone', amount: 90 },
      { code: 'timber', label: 'Timber', amount: 60 },
    ],
    benefit: 'Raises the settlement cap and opens the first civic terrace.',
  },
  {
    id: 'homes-01',
    type: 'homes',
    label: 'Courtyard Homes',
    level: 1,
    gridX: -4,
    gridY: -2,
    orientation: 0,
    variant: 'reed_a',
    districtCode: 'lakeside_outpost',
    visualTier: 0,
    availableBranches: [],
    state: 'idle',
    upgradeCost: [
      { code: 'maize', label: 'Maize', amount: 90 },
      { code: 'timber', label: 'Timber', amount: 70 },
    ],
    benefit: 'Welcomes more residents and adds a planted courtyard.',
  },
  {
    id: 'farm-01',
    type: 'chinampa_farm',
    label: 'Chinampa Farm',
    level: 1,
    gridX: -4,
    gridY: 1,
    orientation: 0,
    variant: 'maize_a',
    districtCode: 'lakeside_outpost',
    visualTier: 0,
    availableBranches: [],
    state: 'producing',
    upgradeCost: [
      { code: 'stone', label: 'Stone', amount: 60 },
      { code: 'timber', label: 'Timber', amount: 55 },
    ],
    benefit: 'Extends irrigation and produces maize for future restoration.',
  },
  {
    id: 'workshop-01',
    type: 'obsidian_workshop',
    label: 'Obsidian Workshop',
    level: 1,
    gridX: 2,
    gridY: -1,
    orientation: 0,
    variant: 'glass_a',
    districtCode: 'lakeside_outpost',
    visualTier: 0,
    availableBranches: [],
    state: 'idle',
    upgradeCost: [
      { code: 'stone', label: 'Stone', amount: 110 },
      { code: 'knowledge', label: 'Knowledge', amount: 1 },
    ],
    benefit: 'Crafts precision tools and prepares advanced Decode aids.',
  },
  {
    id: 'codex-house-01',
    type: 'codex_house',
    label: 'Codex House',
    level: 1,
    gridX: -1,
    gridY: 0,
    orientation: 0,
    variant: 'archive_a',
    districtCode: 'lakeside_outpost',
    visualTier: 0,
    availableBranches: [],
    state: 'idle',
    upgradeCost: [
      { code: 'timber', label: 'Timber', amount: 80 },
      { code: 'knowledge', label: 'Knowledge', amount: 1 },
    ],
    benefit: 'Preserves restored glyphs and reveals family mastery paths.',
  },
  {
    id: 'dock-01',
    type: 'dock',
    label: 'Canoe Dock',
    level: 1,
    gridX: 0,
    gridY: 3,
    orientation: 0,
    variant: 'timber_a',
    districtCode: 'lakeside_outpost',
    visualTier: 0,
    availableBranches: [],
    state: 'idle',
    upgradeCost: [
      { code: 'timber', label: 'Timber', amount: 95 },
      { code: 'maize', label: 'Maize', amount: 60 },
    ],
    benefit: 'Opens longer expedition chains across the lake.',
  },
];

const familySeed: readonly [string, string, string][] = [
  ['sun', 'Sun', 'solar'],
  ['rain', 'Rain', 'cloud'],
  ['maize', 'Maize', 'harvest'],
  ['serpent', 'Serpent', 'river'],
  ['jaguar', 'Jaguar', 'night'],
  ['water', 'Water', 'current'],
];

const codexFamilies: readonly CodexFamilyView[] = familySeed.map(
  ([id, label, motif], familyIndex) => {
    const entries = Array.from({ length: 8 }, (_, entryIndex) => {
      const isFirst = entryIndex === 0;
      const status: CodexEntryView['status'] =
        isFirst && familyIndex === 0
          ? 'found'
          : entryIndex < familyIndex % 3
            ? 'decoded'
            : 'unknown';
      return {
        id: `${id}-${entryIndex + 1}`,
        familyId: id,
        status,
        title:
          status === 'unknown'
            ? undefined
            : isFirst && id === 'sun'
              ? 'Dawn Path'
              : `${label} Sign ${entryIndex + 1}`,
        iconAssetKey: `glyph_${id}_${String(entryIndex + 1).padStart(2, '0')}_line`,
        lorePreview:
          status === 'unknown'
            ? undefined
            : 'A draft fantasy chronicle about knowledge, cultivation and a brighter shared horizon.',
        masteryProgress: status === 'decoded' ? 100 : status === 'found' ? 45 : 0,
        foundAt: status === 'unknown' ? undefined : familyIndex === 0 ? 'Sun Pass' : 'Jade Coast',
        reviewStatus: 'needs_cultural_review' as const,
      };
    });
    const decodedCount = entries.filter((entry) => entry.status === 'decoded').length;
    return {
      id,
      label,
      motif,
      decodedCount,
      totalCount: entries.length,
      masteryTier: 'Awakening',
      entries,
    };
  },
);

const missionSteps: readonly MissionStepView[] = [
  { id: 'discover', label: 'Secure a nearby fragment', complete: false },
  { id: 'decode', label: 'Restore its glyph', complete: false },
  { id: 'build', label: 'Begin a settlement upgrade', complete: false },
];

export const demoVerticalSlice: PlayerVerticalSliceView = {
  schemaVersion: 'vertical-slice.v1',
  source: 'demo_fixture',
  profile: {
    displayName: 'Citlali',
    level: 1,
    locale: 'en',
    avatarAssetId: 'portrait_wayfinder_citlali_v1',
    avatarAssetUrl: '/assets/portraits/wayfinder-citlali-v1.png',
    avatarLabel: 'Dawn Wayfinder',
    onboardingStep: 0,
    onboardingCompleted: false,
  },
  settlement: {
    settlementId: 'demo-settlement-day-1',
    name: 'Lake of First Light',
    level: 1,
    visualTier: 0,
    biome: 'jade_coast_lake',
    resources,
    buildings,
    construction: [],
    districts: [
      {
        code: 'lakeside_outpost',
        displayName: 'Lakeside Outpost',
        unlockOrder: 0,
        bounds: { minX: -4, maxX: 4, minY: -4, maxY: 4 },
        unlocked: true,
        unlockedAt: '2026-09-01T08:00:00Z',
        milestoneSource: 'player_provisioning',
        unlockRequirements: {},
      },
      {
        code: 'garden_quarter',
        displayName: 'Garden Quarter',
        unlockOrder: 1,
        bounds: { minX: 5, maxX: 9, minY: -4, maxY: 4 },
        unlocked: false,
        unlockRequirements: { settlement_tier: 2 },
      },
      {
        code: 'artisan_quarter',
        displayName: 'Artisan Quarter',
        unlockOrder: 2,
        bounds: { minX: -4, maxX: 4, minY: 5, maxY: 9 },
        unlocked: false,
        unlockRequirements: { settlement_tier: 5 },
      },
      {
        code: 'market_quarter',
        displayName: 'Market Quarter',
        unlockOrder: 3,
        bounds: { minX: -9, maxX: -5, minY: -4, maxY: 4 },
        unlocked: false,
        unlockRequirements: { settlement_tier: 9 },
      },
      {
        code: 'ceremonial_quarter',
        displayName: 'Ceremonial Quarter',
        unlockOrder: 4,
        bounds: { minX: -4, maxX: 4, minY: -9, maxY: -5 },
        unlocked: false,
        unlockRequirements: { settlement_tier: 14 },
      },
    ],
    history: [
      {
        id: 'demo-history-shrine-built',
        eventType: 'building_built',
        occurredAt: '2026-09-01T08:15:00Z',
        districtCode: 'lakeside_outpost',
        buildingId: 'solar-shrine-01',
        buildingCode: 'solar_shrine',
        skinCode: 'dawn_a',
        milestoneSource: 'onboarding',
      },
    ],
    worldCapabilities: [
      {
        code: 'seasonal_ritual_discoveries',
        label: 'Seasonal Ritual Discoveries',
        effectiveLevel: 1,
        sourceBuildingId: 'solar-shrine-01',
        sourceBuildingCode: 'solar_shrine',
        parameters: {},
      },
      {
        code: 'complex_fragment_analysis',
        label: 'Complex Fragment Analysis',
        effectiveLevel: 1,
        sourceBuildingId: 'codex-house-01',
        sourceBuildingCode: 'codex_house',
        parameters: {},
      },
      {
        code: 'expedition_chain_capacity',
        label: 'Expedition Chain Capacity',
        effectiveLevel: 1,
        sourceBuildingId: 'dock-01',
        sourceBuildingCode: 'dock',
        parameters: { slots: 1 },
      },
    ],
    ambientPopulation: 9,
    activeMission: {
      id: 'onboarding-restoration-01',
      title: 'The first restoration',
      description: 'Explore, decode and begin one visible settlement upgrade.',
      progress: 0,
      target: 3,
      steps: missionSteps,
      rewardLabel: 'Garden banner and 80 maize',
    },
  },
  explore: {
    regionName: 'Jade Coast',
    center: [4.8952, 52.3702],
    interactionRadiusM: 80,
    maxInteractionAccuracyM: 50,
    uniqueCells: 1,
    journeyActive: false,
    discoveries: [
      {
        id: 'discovery-sun-fragment',
        kind: 'glyph_fragment',
        title: 'Warm stone fragment',
        description: 'A jade-veined fragment waits beside a quiet water garden.',
        longitude: 4.8952,
        latitude: 52.3702,
        state: 'eligible',
        rarity: 'uncommon',
        visualFamily: 'sun',
        worldNodeId: 'world-node-water-garden',
        nodeName: 'Water Garden Archive',
        nodeKind: 'glyph_archive',
        nodeTier: 2,
        activityCode: 'restore_archive_sequence',
        activityTitle: 'Restore the archive sequence',
        activityObjective:
          'Read the carved order, then carry energy through the seals in sequence.',
        activityMechanic: 'archive_sequence',
        routeRole: 'Reveals Codex clues that explain the next linked stop.',
        tierEffect: 'Anchored stones · stronger class identity',
        decodeModifiers: ['basic_rotation', 'sequence_gate', 'locked_anchor'],
        rewardPreview: ['knowledge', 'codex_lore', 'node_attunement'],
      },
      {
        id: 'discovery-river-marker',
        kind: 'ruin',
        title: 'Canal waystone',
        description: 'A carved marker hints at a longer route along the water.',
        longitude: 4.9018,
        latitude: 52.3735,
        state: 'nearby',
        rarity: 'common',
        visualFamily: 'water',
        worldNodeId: 'world-node-canal-gate',
        nodeName: 'Canal Gate',
        nodeKind: 'water_gate',
        nodeTier: 1,
        activityCode: 'balance_water_channels',
        activityTitle: 'Balance the water channels',
        activityObjective:
          'Turn whole rings and individual gates until every channel carries one flow.',
        activityMechanic: 'water_balance',
        routeRole: 'Carries water-family currents toward settlement waterworks.',
        tierEffect: 'One signature rule · short local restoration',
        decodeModifiers: ['basic_rotation', 'ring_rotation'],
        rewardPreview: ['knowledge', 'water_lore', 'node_attunement'],
      },
      {
        id: 'discovery-cloud-marker',
        kind: 'celestial',
        title: 'Cloud alignment',
        description: 'This sighting becomes eligible on a future expedition.',
        longitude: 4.8874,
        latitude: 52.3751,
        state: 'cooldown',
        rarity: 'rare',
        visualFamily: 'rain',
        worldNodeId: 'world-node-cloud-observatory',
        nodeName: 'Cloud Observatory',
        nodeKind: 'celestial_observatory',
        nodeTier: 3,
        activityCode: 'align_sky_cycle',
        activityTitle: 'Align the sky cycle',
        activityObjective:
          'Balance resonant stones so the central glyph and sky seals awaken together.',
        activityMechanic: 'sky_alignment',
        routeRole: 'Opens seasonal route windows without requiring speed or live timing.',
        tierEffect: 'Several terminals · advanced route reasoning',
        decodeModifiers: ['basic_rotation', 'resonance_toggle', 'locked_anchor', 'multi_terminal'],
        rewardPreview: ['knowledge', 'seasonal_lore', 'node_attunement'],
      },
    ],
    energyLinks: [
      {
        id: 'knowledge-current-water-canal',
        sourceNodeId: 'world-node-canal-gate',
        destinationNodeId: 'world-node-cloud-observatory',
        sourceCoordinates: [4.9018, 52.3735],
        destinationCoordinates: [4.8874, 52.3751],
        familyCode: 'rain',
        requiredTier: 1,
        state: 'awakened',
      },
      {
        id: 'knowledge-current-water-cloud',
        sourceNodeId: 'world-node-water-garden',
        destinationNodeId: 'world-node-canal-gate',
        sourceCoordinates: [4.8952, 52.3702],
        destinationCoordinates: [4.9018, 52.3735],
        familyCode: 'water',
        requiredTier: 2,
        state: 'dormant',
      },
    ],
    routeContracts: [
      {
        id: 'world-route-water-cloud',
        title: 'Water Garden Archive to Cloud Observatory',
        familyCode: 'water',
        distanceM: 1480,
        requiredTier: 2,
        state: 'available',
        currentStep: 0,
        nextNodeId: 'world-node-water-garden',
        stops: [
          {
            sequence: 1,
            nodeId: 'world-node-water-garden',
            name: 'Water Garden Archive',
            nodeKind: 'glyph_archive',
            nodeTier: 2,
            coordinates: [4.8952, 52.3702],
            state: 'next',
          },
          {
            sequence: 2,
            nodeId: 'world-node-canal-gate',
            name: 'Canal Gate',
            nodeKind: 'water_gate',
            nodeTier: 1,
            coordinates: [4.9018, 52.3735],
            state: 'locked',
          },
          {
            sequence: 3,
            nodeId: 'world-node-cloud-observatory',
            name: 'Cloud Observatory',
            nodeKind: 'celestial_observatory',
            nodeTier: 3,
            coordinates: [4.8874, 52.3751],
            state: 'locked',
          },
        ],
      },
    ],
  },
  decode: {
    runId: 'decode-demo-sun-01',
    status: 'ready',
    currentMoves: 0,
    hintCount: 0,
    profileCode: 'tutorial',
    modifiers: [],
    masteryTargets: ['solve'],
    puzzle: {
      id: 'tutorial-sun-01',
      definitionVersion: 4,
      seed: 'azr-hero-tutorial-001',
      familyId: 'sun',
      familyLabel: 'Sun',
      ringCount: 2,
      sectorsPerRing: [3, 4],
      sourceSegmentIds: ['segment-1'],
      terminalSegmentIds: ['segment-5'],
      parMoves: 3,
      minimumSolutionMoves: 3,
      difficultyEstimate: 5,
      tutorialSteps: [
        {
          sequence: 1,
          segmentId: 'segment-1',
          direction: 'clockwise',
          concept: 'wake_source',
        },
        {
          sequence: 2,
          segmentId: 'segment-4',
          direction: 'clockwise',
          concept: 'carry_energy',
        },
        {
          sequence: 3,
          segmentId: 'segment-4',
          direction: 'clockwise',
          concept: 'reach_seal',
        },
      ],
      reviewStatus: 'needs_cultural_review',
      segments: Array.from({ length: 7 }, (_, index) => {
        const id = `segment-${index + 1}`;
        const isSource = index === 0;
        const isTurningStone = index === 3;
        const isTerminal = index === 4;
        return {
          id,
          ringIndex: index < 3 ? 0 : 1,
          angularSlot: index < 3 ? index : index - 3,
          rotation: isSource ? 3 : isTurningStone ? 2 : isTerminal ? 2 : 1,
          targetRotation: isSource || isTurningStone ? 0 : isTerminal ? 2 : 1,
          locked: false,
          channelShape: isTurningStone || isTerminal ? ('corner' as const) : ('straight' as const),
        };
      }),
    },
    rewardPreview: [
      { code: 'maize', label: 'Maize', amount: 120 },
      { code: 'stone', label: 'Stone', amount: 60 },
      { code: 'knowledge', label: 'Knowledge', amount: 1 },
    ],
  },
  decodeProfiles: [
    {
      code: 'tutorial',
      version: 2,
      ringCount: 2,
      sectorsPerRing: [3, 4],
      modifiers: [],
      masteryTargets: ['solve'],
      minimumSolutionMoves: 3,
    },
    {
      code: 'common',
      version: 2,
      ringCount: 3,
      sectorsPerRing: [4, 5, 6],
      modifiers: ['locked_anchor'],
      masteryTargets: ['solve', 'no_hint'],
      minimumSolutionMoves: 8,
    },
    {
      code: 'advanced',
      version: 2,
      ringCount: 4,
      sectorsPerRing: [4, 5, 6, 7],
      modifiers: ['locked_anchor', 'ring_rotation', 'multi_terminal'],
      masteryTargets: ['solve', 'no_hint', 'par_moves'],
      minimumSolutionMoves: 24,
    },
    {
      code: 'mastery',
      version: 2,
      ringCount: 5,
      sectorsPerRing: [4, 5, 6, 7, 8],
      modifiers: ['locked_anchor', 'ring_rotation', 'multi_terminal', 'eclipse'],
      masteryTargets: ['solve', 'no_hint', 'par_moves', 'resonance'],
      minimumSolutionMoves: 28,
    },
  ],
  decodeMastery: [
    {
      familyCode: 'sun',
      profileCode: 'tutorial',
      attempts: 1,
      completions: 0,
      noHintCompletions: 0,
      bestScore: 0,
      masteryPoints: 0,
      masteryTier: 'awakening',
    },
  ],
  codexFamilies,
  soloPaths: [
    {
      id: 'explorer',
      label: 'Explorer',
      description: 'Reveal distinct regions and complete safe expedition chains.',
      progress: 24,
      nextMilestone: 'Chart three unique cells',
    },
    {
      id: 'artisan',
      label: 'Artisan',
      description: 'Restore complex glyphs and improve civic craft.',
      progress: 16,
      nextMilestone: 'Decode the Dawn Path',
    },
    {
      id: 'merchant',
      label: 'Merchant',
      description: 'Fulfil independent orders without relying on a Tribe.',
      progress: 8,
      nextMilestone: 'Open the market terrace',
    },
  ],
  solo: {
    independentOfTribe: true,
    currentRank: 'wayfinder',
    ranks: [
      {
        code: 'wayfinder',
        displayName: 'Wayfinder',
        ordinal: 0,
        eligible: true,
        requirements: { account_age_days: 0 },
        progress: { account_age_days: 0 },
      },
      {
        code: 'artisan',
        displayName: 'Artisan',
        ordinal: 1,
        eligible: false,
        requirements: { decode_mastery_points: 3, codex_entries: 1, contracts_completed: 1 },
        progress: { decode_mastery_points: 0, codex_entries: 0, contracts_completed: 0 },
      },
    ],
    contracts: [
      {
        id: 'demo-contract-artisan',
        code: 'solo_decode_weekly',
        specialization: 'artisan',
        event: 'decode_completed',
        current: 0,
        target: 3,
        status: 'active',
        rewards: [{ code: 'knowledge', label: 'Knowledge', amount: 12 }],
      },
    ],
    merchantOrders: [
      {
        id: 'demo-order-maize',
        code: 'merchant_maize_caravan',
        specialization: 'merchant',
        resourceCode: 'maize',
        unitCost: 20,
        fulfilledQuantity: 0,
        targetQuantity: 3,
        status: 'active',
        completionRewards: [{ code: 'knowledge', label: 'Knowledge', amount: 10 }],
      },
    ],
  },
  missions: [
    {
      id: 'demo-daily-decode',
      code: 'daily_01',
      scope: 'daily',
      event: 'decode_completed',
      current: 0,
      target: 2,
      status: 'active',
      periodKey: '2026-09-06',
    },
  ],
  tribe: { discovered: [], feed: [], leaderboard: [] },
  featureFlags: {
    exploreEnabled: true,
    tribesEnabled: false,
    paymentsEnabled: false,
    rewardExternalizationEnabled: false,
  },
};

export type VerticalSliceCommand =
  | { type: 'decode.weave.start'; commandId: string; weaveId: string }
  | { type: 'decode.weave.leave'; commandId: string }
  | { type: 'explore.claim'; commandId: string; discoveryId: string }
  | { type: 'explore.remote'; commandId: string }
  | {
      type: 'decode.start';
      commandId: string;
      profileCode: 'tutorial' | 'common' | 'advanced' | 'mastery';
    }
  | {
      type: 'decode.hint';
      commandId: string;
      moves: readonly DecodeMoveView[];
    }
  | {
      type: 'decode.complete';
      commandId: string;
      moves: readonly DecodeMoveView[];
      clientCompletionTimeMs: number;
    }
  | { type: 'settlement.upgrade'; commandId: string; buildingId: string; branchCode?: string }
  | { type: 'settlement.complete'; commandId: string; constructionId: string }
  | { type: 'solo.contract.claim'; commandId: string; contractId: string }
  | { type: 'solo.order.fulfill'; commandId: string; orderId: string; quantity: number }
  | { type: 'tribe.create'; commandId: string; name: string }
  | { type: 'tribe.join'; commandId: string; tribeId: string }
  | { type: 'tribe.leave'; commandId: string; tribeId: string }
  | {
      type: 'tribe.member.role';
      commandId: string;
      tribeId: string;
      memberId: string;
      role: 'member' | 'veteran' | 'officer' | 'leader';
    }
  | {
      type: 'tribe.contribute';
      commandId: string;
      tribeId: string;
      projectId: string;
      resourceCode: ResourceCode;
      amount: number;
    };

const commandReceipts = new Map<string, PlayerVerticalSliceView>();

const visualFixtureNames = new Set([
  'loading',
  'not-found',
  'world-glimpse',
  'day-1',
  'mature',
  'new-discovery',
  'passive',
  'tutorial',
  'mid-game',
  'solved-reward',
  'collection',
  'advanced',
  'overview',
  'project',
  'tribe-phase-1',
  'tribe-phase-2',
  'tribe-phase-3',
  'tribe-phase-4',
  'tribe-phase-5',
  'construction-0',
  'construction-25',
  'construction-50',
  'construction-75',
  'construction-100',
  'district-expansion',
  'completed-specialization',
  'pass',
  'reduced-motion',
]);

export function visualFixtureName(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const e2e = (window as Window & { __AZR_E2E__?: { deterministic?: boolean } }).__AZR_E2E__;
  if (!e2e?.deterministic) return undefined;
  const fixture = new URLSearchParams(window.location.search).get('fixture') ?? undefined;
  return fixture && visualFixtureNames.has(fixture) ? fixture : undefined;
}

function midGameDecodePuzzle(): DecodePuzzleDefinition {
  const sectorsPerRing = [4, 6, 8, 10] as const;
  const channelShapes = ['straight', 'corner', 'fork'] as const;
  const lockedSegments = new Set(['mid-segment-2', 'mid-segment-11', 'mid-segment-23']);
  const interruptedSegments = new Set([
    'mid-segment-5',
    'mid-segment-8',
    'mid-segment-14',
    'mid-segment-18',
    'mid-segment-26',
  ]);
  let segmentNumber = 0;
  const segments = sectorsPerRing.flatMap((slots, ringIndex) =>
    Array.from({ length: slots }, (_, angularSlot) => {
      segmentNumber += 1;
      const id = `mid-segment-${segmentNumber}`;
      const channelShape = channelShapes[(segmentNumber + ringIndex) % channelShapes.length];
      const targetRotation =
        ringIndex === 0 || channelShape === 'straight' || angularSlot % 3 !== 0 ? 0 : 3;
      return {
        id,
        ringIndex,
        angularSlot,
        rotation: interruptedSegments.has(id) ? (targetRotation + 1) % 4 : targetRotation,
        targetRotation,
        locked: lockedSegments.has(id),
        channelShape: channelShape ?? 'straight',
      };
    }),
  );

  return {
    id: 'mid-game-celestial-01',
    seed: 'azr-mid-celestial-024',
    familyId: 'sun',
    familyLabel: 'Celestial Sun',
    ringCount: sectorsPerRing.length,
    sectorsPerRing,
    terminalSegmentIds: ['mid-segment-19', 'mid-segment-24'],
    rotatableRingIndices: [1, 3],
    moveLimit: 16,
    parMoves: 12,
    minimumSolutionMoves: 12,
    difficultyEstimate: 28,
    reviewStatus: 'needs_cultural_review',
    segments,
  };
}

function visualFixtureSnapshot(fixture: string): PlayerVerticalSliceView {
  const snapshot = cloneView(demoVerticalSlice);
  if (fixture === 'day-1') {
    const starterTypes = new Set(['solar_shrine', 'homes', 'chinampa_farm', 'obsidian_workshop']);
    snapshot.settlement.buildings = snapshot.settlement.buildings.filter((building) =>
      starterTypes.has(building.type),
    );
    snapshot.settlement.ambientPopulation = 12;
  }
  const constructionFixture = fixture.match(/^construction-(0|25|50|75|100)$/);
  if (constructionFixture) {
    const progress = Number(constructionFixture[1]) / 100;
    const building = snapshot.settlement.buildings[0];
    if (building) {
      building.state = 'constructing';
      building.constructionTargetLevel = Math.min(building.maxLevel ?? 5, building.level + 1);
      building.progress = progress;
      snapshot.settlement.construction = [
        {
          id: `visual-construction-${constructionFixture[1]}`,
          buildingId: building.id,
          progress,
          remainingLabel: progress >= 1 ? 'Ready now' : 'Authoritative fixture window',
        },
      ];
    }
  }
  if (fixture === 'mature') {
    snapshot.settlement.level = 4;
    snapshot.settlement.visualTier = 3;
    snapshot.settlement.ambientPopulation = 64;
    snapshot.settlement.name = 'Gardens of the Bright Horizon';
    snapshot.settlement.buildings = snapshot.settlement.buildings.map((building, index) => ({
      ...building,
      level: Math.min(5, 3 + (index % 3)),
      visualTier: Math.min(3, 2 + (index % 2)) as 0 | 1 | 2 | 3,
      branchCode: building.type === 'solar_shrine' ? 'waterworks' : building.branchCode,
      state: 'idle',
    }));
    snapshot.settlement.districts = snapshot.settlement.districts.map((district) => ({
      ...district,
      unlocked: true,
      unlockedAt: `2026-09-0${district.unlockOrder + 1}T08:00:00Z`,
      milestoneSource: district.unlockOrder === 0 ? 'player_provisioning' : 'settlement_tier',
    }));
  }
  if (fixture === 'district-expansion') {
    snapshot.settlement.districts = snapshot.settlement.districts.map((district) => ({
      ...district,
      unlocked: district.unlockOrder <= 1,
      unlockedAt: district.unlockOrder <= 1 ? '2026-09-07T10:00:00Z' : undefined,
      milestoneSource: district.unlockOrder <= 1 ? 'settlement_tier' : undefined,
    }));
    snapshot.settlement.history = [
      {
        id: 'fixture-history-garden-quarter',
        eventType: 'district_unlocked',
        occurredAt: '2026-09-07T10:00:00Z',
        districtCode: 'garden_quarter',
        milestoneSource: 'settlement_tier',
      },
      ...snapshot.settlement.history,
    ];
  }
  if (fixture === 'completed-specialization') {
    const shrine = snapshot.settlement.buildings.find(
      (building) => building.type === 'solar_shrine',
    );
    if (shrine) {
      shrine.level = 3;
      shrine.visualTier = 1;
      shrine.branchCode = 'waterworks';
      shrine.state = 'idle';
      snapshot.settlement.worldCapabilities = [
        ...snapshot.settlement.worldCapabilities,
        {
          code: 'waterway_discoveries',
          label: 'Waterway Discoveries',
          effectiveLevel: 3,
          sourceBuildingId: shrine.id,
          sourceBuildingCode: 'solar_shrine',
          specializationCode: 'waterworks',
          parameters: {},
        },
      ];
      snapshot.settlement.history = [
        {
          id: 'fixture-history-waterworks',
          eventType: 'specialization_selected',
          occurredAt: '2026-09-07T11:00:00Z',
          districtCode: shrine.districtCode,
          buildingId: shrine.id,
          buildingCode: 'solar_shrine',
          specializationCode: 'waterworks',
          skinCode: shrine.variant,
          milestoneSource: 'construction_specialization',
        },
        ...snapshot.settlement.history,
      ];
    }
  }
  if (fixture === 'passive') {
    snapshot.explore.journeyActive = true;
    snapshot.explore.journeyId = 'visual-fixture-journey';
    snapshot.explore.discoveries = snapshot.explore.discoveries.map((discovery, index) =>
      index < 2 ? { ...discovery, state: 'queued' } : discovery,
    );
  }
  if (fixture === 'tutorial') {
    snapshot.decode.runId = 'decode-visual-tutorial';
    snapshot.decode.status = 'active';
    snapshot.decode.currentMoves = 0;
  }
  if (fixture === 'mid-game') {
    const profile = snapshot.decodeProfiles.find((item) => item.code === 'advanced');
    snapshot.decode.runId = 'decode-visual-mid-game';
    snapshot.decode.status = 'active';
    snapshot.decode.profileCode = 'advanced';
    snapshot.decode.modifiers = profile?.modifiers ?? [];
    snapshot.decode.masteryTargets = profile?.masteryTargets ?? [];
    snapshot.decode.currentMoves = 7;
    snapshot.decode.puzzle = midGameDecodePuzzle();
    snapshot.decode.rewardPreview = [
      { code: 'stone', label: 'Stone', amount: 180 },
      { code: 'jade', label: 'Jade', amount: 6 },
      { code: 'knowledge', label: 'Knowledge', amount: 2 },
    ];
  }
  if (fixture === 'solved-reward') {
    snapshot.decode.runId = 'decode-visual-solved-reward';
    snapshot.decode.status = 'solved';
    snapshot.decode.currentMoves = 5;
    snapshot.decode.puzzle.segments = snapshot.decode.puzzle.segments.map((segment) => ({
      ...segment,
      rotation: segment.targetRotation ?? segment.rotation,
    }));
    snapshot.settlement.resources = snapshot.settlement.resources.map((resource) => ({
      ...resource,
      amount:
        resource.amount +
        (snapshot.decode.rewardPreview.find((reward) => reward.code === resource.code)?.amount ??
          0),
    }));
    snapshot.decodeMastery = [
      {
        familyCode: 'sun',
        profileCode: 'tutorial',
        attempts: 1,
        completions: 1,
        noHintCompletions: 1,
        bestScore: 980,
        masteryPoints: 4,
        masteryTier: 'radiance',
      },
    ];
  }
  if (fixture === 'collection') {
    snapshot.codexFamilies = snapshot.codexFamilies.map((family, familyIndex) => ({
      ...family,
      decodedCount: 5 - (familyIndex % 3),
      masteryTier: familyIndex === 0 ? 'Radiance' : 'Awakening',
      entries: family.entries.map((entry, entryIndex) =>
        entryIndex < 5 - (familyIndex % 3)
          ? {
              ...entry,
              title: entry.title ?? `${family.label} Sign ${entryIndex + 1}`,
              status: entryIndex === 0 ? 'mastered' : 'decoded',
              masteryProgress: 100,
            }
          : entry,
      ),
    }));
  }
  if (fixture === 'advanced') {
    snapshot.solo.currentRank = 'master_keeper';
    snapshot.solo.ranks = snapshot.solo.ranks.map((rank) => ({ ...rank, eligible: true }));
    snapshot.solo.contracts = snapshot.solo.contracts.map((contract) => ({
      ...contract,
      current: contract.target,
      status: 'completed',
    }));
    snapshot.solo.merchantOrders = snapshot.solo.merchantOrders.map((order) => ({
      ...order,
      fulfilledQuantity: order.targetQuantity - 1,
    }));
  }
  const tribePhaseFixture = fixture.match(/^tribe-phase-([1-5])$/);
  if (fixture === 'overview' || fixture === 'project' || tribePhaseFixture) {
    const tribePhase = tribePhaseFixture ? Number(tribePhaseFixture[1]) : 3;
    const phaseProgress = [0, 0.18, 0.38, 0.68, 0.86, 1][tribePhase] ?? 0;
    snapshot.featureFlags.tribesEnabled = true;
    snapshot.tribe = {
      current: {
        id: 'visual-tribe',
        name: 'People of the Jade Water',
        slug: 'people-of-the-jade-water',
        leaderMemberId: 'visual-leader',
        reputation: 12_480,
        rankTier: 'Civic IV',
        memberCount: 18,
        currentUserRole: 'officer',
        project: {
          id: 'visual-project',
          title: 'Great Civic Complex',
          phase: tribePhase,
          phaseCount: 5,
          progress: phaseProgress,
          state: 'active',
          remainingSeconds: 1_224_000,
          visualStageAssetKey: `tribe_great_civic_complex_phase_${tribePhase}`,
          requirements: [
            { resourceCode: 'maize', label: 'Maize', current: 180, target: 300 },
            { resourceCode: 'stone', label: 'Stone', current: 190, target: 240 },
            { resourceCode: 'timber', label: 'Timber', current: 120, target: 180 },
            { resourceCode: 'jade', label: 'Jade', current: 6, target: 15 },
          ],
        },
        members: [
          {
            memberId: 'visual-leader',
            displayName: 'Ixchel',
            role: 'leader',
            contributionScore: 248,
            joinedAt: '2026-08-01T10:00:00Z',
          },
          {
            memberId: 'visual-member',
            displayName: 'Cuauhli',
            role: 'veteran',
            contributionScore: 196,
            joinedAt: '2026-08-04T10:00:00Z',
          },
        ],
      },
      discovered: [],
      feed: [
        {
          id: 'visual-feed-1',
          eventType: 'project_contributed',
          actorMemberId: 'visual-member',
          payload: { resource_code: 'stone', amount: 40 },
          createdAt: '2026-09-06T08:45:00Z',
        },
        {
          id: 'visual-feed-2',
          eventType: 'project_phase_completed',
          payload: { phase: 2 },
          createdAt: '2026-09-05T18:20:00Z',
        },
      ],
      leaderboard: [
        { rank: 1, entityId: 'visual-leader', displayName: 'Ixchel', score: 248 },
        { rank: 2, entityId: 'visual-member', displayName: 'Cuauhli', score: 196 },
      ],
      leaderboardPeriod: '2026-W36',
    };
  }
  return snapshot;
}

export function demoModeEnabled(): boolean {
  return (
    import.meta.env.VITE_DATA_ADAPTER !== 'api' &&
    (import.meta.env.MODE === 'test' || import.meta.env.VITE_DEMO_MODE === 'true')
  );
}

function cloneView(view: PlayerVerticalSliceView): PlayerVerticalSliceView {
  return structuredClone(view);
}

function updateMission(
  view: PlayerVerticalSliceView,
  stepId: MissionStepView['id'],
): PlayerVerticalSliceView {
  const steps = view.settlement.activeMission.steps.map((step) =>
    step.id === stepId ? { ...step, complete: true } : step,
  );
  return {
    ...view,
    settlement: {
      ...view.settlement,
      activeMission: {
        ...view.settlement.activeMission,
        progress: steps.filter((step) => step.complete).length,
        steps,
      },
    },
  };
}

function adjustResources(
  current: readonly ResourceDisplay[],
  changes: Partial<Record<ResourceCode, number>>,
): readonly ResourceDisplay[] {
  return current.map((resource) => ({
    ...resource,
    amount: resource.amount + (changes[resource.code] ?? 0),
  }));
}

function applyDemoCommand(
  current: PlayerVerticalSliceView,
  command: VerticalSliceCommand,
): PlayerVerticalSliceView {
  if (command.type === 'explore.claim') {
    const discovery = current.explore.discoveries.find((item) => item.id === command.discoveryId);
    if (!discovery || !['eligible', 'queued'].includes(discovery.state)) {
      throw new Error('This discovery is not eligible for a safe claim.');
    }
    const next = {
      ...current,
      explore: {
        ...current.explore,
        discoveries: current.explore.discoveries.map((item) =>
          item.id === command.discoveryId ? { ...item, state: 'claimed' as const } : item,
        ),
      },
      decode: { ...current.decode, status: 'active' as const },
    };
    return updateMission(next, 'discover');
  }

  if (command.type === 'explore.remote') {
    const profile = current.decodeProfiles.find((item) => item.code === 'advanced');
    if (!profile) throw new Error('The daily Codex expedition is unavailable.');
    return {
      ...current,
      decode: {
        ...current.decode,
        runId: 'decode-demo-remote-expedition',
        status: 'active',
        profileCode: profile.code,
        modifiers: profile.modifiers,
        masteryTargets: profile.masteryTargets,
      },
    };
  }

  if (command.type === 'decode.weave.start' || command.type === 'decode.weave.leave') {
    throw new Error('Knowledge Weave challenges require the live World and verified visits.');
  }

  if (command.type === 'decode.start') {
    const profile = current.decodeProfiles.find((item) => item.code === command.profileCode);
    if (!profile) throw new Error('That Decode profile is unavailable.');
    return {
      ...current,
      decode: {
        ...current.decode,
        runId: `decode-demo-${command.profileCode}`,
        status: 'active',
        profileCode: command.profileCode,
        modifiers: profile.modifiers,
        masteryTargets: profile.masteryTargets,
      },
    };
  }

  if (command.type === 'decode.hint') {
    const rotations = replayRotations(current, command.moves);
    const segment = current.decode.puzzle.segments.find(
      (item) => item.targetRotation !== undefined && rotations[item.id] !== item.targetRotation,
    );
    if (!segment || segment.targetRotation === undefined) {
      throw new Error('The puzzle is already aligned.');
    }
    const clockwiseDistance = (segment.targetRotation - (rotations[segment.id] ?? 0) + 4) % 4;
    const direction = clockwiseDistance <= 2 ? 'clockwise' : 'counter_clockwise';
    const suggestedRotation =
      ((rotations[segment.id] ?? 0) + (direction === 'clockwise' ? 1 : -1) + 4) % 4;
    return {
      ...current,
      decode: {
        ...current.decode,
        hintCount: current.decode.hintCount + 1,
        suggestedMove: {
          segmentId: segment.id,
          direction,
          completesPuzzle: current.decode.puzzle.segments.every(
            (item) =>
              item.targetRotation !== undefined &&
              (item.id === segment.id ? suggestedRotation : rotations[item.id]) ===
                item.targetRotation,
          ),
        },
      },
    };
  }

  if (command.type === 'decode.complete') {
    const rotations = replayRotations(current, command.moves);
    const valid = current.decode.puzzle.segments.every(
      (segment) =>
        segment.targetRotation !== undefined && rotations[segment.id] === segment.targetRotation,
    );
    if (!valid) throw new Error('The restored path is not yet valid.');
    if (current.decode.status === 'solved') return current;

    const rewardChanges = Object.fromEntries(
      current.decode.rewardPreview.map((reward) => [reward.code, reward.amount]),
    ) as Partial<Record<ResourceCode, number>>;
    const next = {
      ...current,
      decode: {
        ...current.decode,
        status: 'solved' as const,
        currentMoves: command.moves.length,
        suggestedMove: undefined,
        puzzle: {
          ...current.decode.puzzle,
          segments: current.decode.puzzle.segments.map((segment) => ({
            ...segment,
            rotation: rotations[segment.id] ?? segment.rotation,
          })),
        },
      },
      settlement: {
        ...current.settlement,
        resources: adjustResources(current.settlement.resources, rewardChanges),
      },
      codexFamilies: current.codexFamilies.map((family) => {
        if (family.id !== current.decode.puzzle.familyId) return family;
        const entries = family.entries.map((entry, index) =>
          index === 0 ? { ...entry, status: 'decoded' as const, masteryProgress: 100 } : entry,
        );
        return {
          ...family,
          decodedCount: entries.filter((entry) => entry.status === 'decoded').length,
          entries,
        };
      }),
    };
    return updateMission(next, 'decode');
  }

  if (command.type === 'settlement.upgrade') {
    const building = current.settlement.buildings.find((item) => item.id === command.buildingId);
    if (!building) throw new Error('That structure could not be found.');
    if (building.state === 'constructing') return current;
    const balances = new Map(
      current.settlement.resources.map((resource) => [resource.code, resource.amount]),
    );
    if (building.upgradeCost.some((cost) => (balances.get(cost.code) ?? 0) < cost.amount)) {
      throw new Error('The realm has not gathered enough resources for this upgrade.');
    }
    const costs = Object.fromEntries(
      building.upgradeCost.map((cost) => [cost.code, -cost.amount]),
    ) as Partial<Record<ResourceCode, number>>;
    const constructionId = `construction-${building.id}-${building.level + 1}`;
    const next = {
      ...current,
      settlement: {
        ...current.settlement,
        resources: adjustResources(current.settlement.resources, costs),
        buildings: current.settlement.buildings.map((item) =>
          item.id === building.id
            ? {
                ...item,
                branchCode: command.branchCode ?? item.branchCode,
                state: 'constructing' as const,
                constructionTargetLevel: building.level + 1,
                visualTier: Math.min(3, Math.floor(building.level / 2)) as 0 | 1 | 2 | 3,
                progress: 0.18,
              }
            : item,
        ),
        construction: [
          ...current.settlement.construction,
          {
            id: constructionId,
            buildingId: building.id,
            progress: 0.18,
            remainingLabel: '18 seconds',
          },
        ],
      },
    };
    return updateMission(next, 'build');
  }

  if (command.type === 'solo.order.fulfill') {
    return {
      ...current,
      solo: {
        ...current.solo,
        merchantOrders: current.solo.merchantOrders.map((order) =>
          order.id === command.orderId
            ? {
                ...order,
                fulfilledQuantity: Math.min(
                  order.targetQuantity,
                  order.fulfilledQuantity + command.quantity,
                ),
                status:
                  order.fulfilledQuantity + command.quantity >= order.targetQuantity
                    ? 'completed'
                    : order.status,
              }
            : order,
        ),
      },
    };
  }

  if (command.type === 'solo.contract.claim') {
    return {
      ...current,
      solo: {
        ...current.solo,
        contracts: current.solo.contracts.map((contract) =>
          contract.id === command.contractId && contract.status === 'completed'
            ? { ...contract, status: 'claimed' }
            : contract,
        ),
      },
    };
  }

  if (command.type.startsWith('tribe.')) return current;

  if (command.type !== 'settlement.complete') return current;

  const construction = current.settlement.construction.find(
    (item) => item.id === command.constructionId,
  );
  if (!construction) return current;
  return {
    ...current,
    settlement: {
      ...current.settlement,
      visualTier: current.settlement.visualTier === 0 ? 1 : current.settlement.visualTier,
      buildings: current.settlement.buildings.map((building) =>
        building.id === construction.buildingId
          ? {
              ...building,
              level: building.level + 1,
              visualTier: Math.min(3, Math.floor(building.level / 2)) as 0 | 1 | 2 | 3,
              state: 'idle' as const,
              constructionTargetLevel: undefined,
              progress: undefined,
            }
          : building,
      ),
      construction: current.settlement.construction.filter((item) => item.id !== construction.id),
    },
  };
}

function replayRotations(
  current: PlayerVerticalSliceView,
  moves: readonly DecodeMoveView[],
): Record<string, number> {
  const rotations = Object.fromEntries(
    current.decode.puzzle.segments.map((segment) => [segment.id, segment.rotation]),
  );
  for (const move of moves) {
    const step = move.direction === 'clockwise' ? 1 : -1;
    if (move.moveType === 'rotate_ring') {
      if (move.ringIndex === undefined) throw new Error('A Decode ring move has no ring.');
      for (const segment of current.decode.puzzle.segments) {
        if (segment.ringIndex !== move.ringIndex) continue;
        rotations[segment.id] = ((rotations[segment.id] ?? segment.rotation) + step + 4) % 4;
      }
      continue;
    }
    if (!move.segmentId) throw new Error('A Decode segment move has no stone.');
    const rotation = rotations[move.segmentId];
    if (rotation === undefined) throw new Error('A Decode move references an unknown segment.');
    rotations[move.segmentId] = (rotation + step + 4) % 4;
  }
  return rotations;
}

export async function loadVerticalSlice(signal?: AbortSignal): Promise<PlayerVerticalSliceView> {
  const fixture = visualFixtureName();
  if (fixture === 'loading') return new Promise(() => undefined);
  if (fixture) return visualFixtureSnapshot(fixture);
  if (demoModeEnabled()) return cloneView(demoVerticalSlice);
  return loadApiVerticalSlice(signal);
}

export async function submitVerticalSliceCommand(
  current: PlayerVerticalSliceView,
  command: VerticalSliceCommand,
): Promise<PlayerVerticalSliceView> {
  if (!demoModeEnabled()) {
    return submitApiVerticalSliceCommand(current, command);
  }
  const receipt = commandReceipts.get(command.commandId);
  if (receipt) return cloneView(receipt);
  const next = applyDemoCommand(current, command);
  commandReceipts.set(command.commandId, next);
  return cloneView(next);
}
